'use client';

import { useCallback, useRef, useSyncExternalStore } from 'react';

/**
 * State backed by a single localStorage key, kept in sync across every consumer:
 *  1. Same-tab: a custom `changedEvent` fired by `writeSyncedLocalStorage`
 *  2. Cross-tab: the native `storage` event fired in OTHER tabs on write
 *
 * `read` must be a stable (module-level) closure that returns the parsed value,
 * falling back to a default during SSR (`typeof window === 'undefined'`).
 *
 * The same `getSnapshot` is passed as both the client and server snapshot, so
 * the very first hydration render already reflects localStorage rather than the
 * SSR default — this avoids a default→stored flash on every mount, at the cost
 * of a hydration mismatch for any key whose stored value differs from the
 * default. That tradeoff is intentional and acceptable here: these are
 * client-only settings panels with no first-paint/SEO contract. Snapshots are
 * cached on the raw stored string so object-valued keys stay referentially
 * stable between renders, as `useSyncExternalStore` requires.
 */
export function useSyncedLocalStorage<T>(
  key: string,
  changedEvent: string,
  read: () => T,
): T {
  const cache = useRef<{ raw: string | null; value: T } | null>(null);

  const getSnapshot = useCallback((): T => {
    const raw = typeof window === 'undefined' ? null : localStorage.getItem(key);
    if (cache.current && cache.current.raw === raw) return cache.current.value;
    const value = read();
    cache.current = { raw, value };
    return value;
  }, [key, read]);

  const subscribe = useCallback(
    (onStoreChange: () => void): (() => void) => {
      function onStorageEvent(e: StorageEvent): void {
        if (e.key === key || e.key === null) onStoreChange();
      }
      window.addEventListener(changedEvent, onStoreChange);
      window.addEventListener('storage', onStorageEvent);
      return () => {
        window.removeEventListener(changedEvent, onStoreChange);
        window.removeEventListener('storage', onStorageEvent);
      };
    },
    [key, changedEvent],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Write a value to a synced localStorage key and notify same-tab consumers.
 * Pass `null` to remove the key. `value` is the already-serialized string the
 * caller wants persisted (JSON, an enum member, etc.).
 */
export function writeSyncedLocalStorage(
  key: string,
  changedEvent: string,
  value: string | null,
): void {
  if (value === null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, value);
  }
  window.dispatchEvent(new Event(changedEvent));
}
